import { TEST_IDS } from '@va/constants';
import { CheckMarkWithFilledBackground, FilterIcon, RightArrowIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Filter, FilterTypes, isFilter, isNestedFiltersPage, NestedFiltersPage } from '@va/types/filters';
import { SearchFilterV2 } from '@va/ui/components/inputs';
import { MenuList, MenuListItemType, ToggleSwitch } from '@va/ui/design-system';
import { useCallback, useMemo, useState } from 'react';
import { FilterOptions, useFiltersContext } from '../filters-context';
import { FilterActionButtons } from './FilterActionButtons';
import { SubFilterTabHeader } from './SubFilterTabHeader';

export const AvailableFiltersList = ({
  availableFilters,
  onFilterClick,
  closeDropdown,
  onBackClick,
  title,
}: {
  availableFilters: FilterOptions;
  onFilterClick: (filter: Filter | NestedFiltersPage) => void;
  closeDropdown: () => void;
  onBackClick?: () => void;
  title?: string;
}) => {
  const [filterValue, setFilterValue] = useState('');

  const { shouldHighlightFilter, isFilterApplied } = useFiltersContext();

  const translate = useTranslate();

  const filteredOptions = useMemo(() => {
    if (!filterValue) return availableFilters;

    const filters: FilterOptions = [];

    const findRec = (options: FilterOptions) => {
      options.forEach((item) => {
        const isNested = isNestedFiltersPage(item);
        const labelMatches = item.label.toLowerCase().includes(filterValue.toLowerCase());

        //If filter not nested and label matches, add filter to the array;
        if (labelMatches) {
          if (
            !isNested ||
            item.filters.every((filter) => !filter.label.toLowerCase().includes(filterValue.toLowerCase()))
          ) {
            filters.push(isNested ? (item as NestedFiltersPage) : (item as Filter));
          }
        }

        //If filter is nested, add parent filter to array if searchValue doesn't match any child filter
        if (isNested) {
          findRec(item.filters);
        }
      });
    };

    findRec(availableFilters);

    return filters;
  }, [availableFilters, filterValue]);

  const renderIcon = useCallback(
    (filter: NestedFiltersPage | Filter) => {
      const appliedFilter = isFilterApplied(filter.id);

      if (isFilter(filter) && filter.type === FilterTypes.Boolean) {
        return (
          <ToggleSwitch className='ml-auto' checked={!!appliedFilter?.values[0]} onChange={() => {}} size='small' />
        );
      }

      if (appliedFilter) {
        return <CheckMarkWithFilledBackground className='ml-auto' width='16px' height='16px' />;
      }

      return <RightArrowIcon className='ml-auto shrink-0 w-4 h-4' color='#969696' />;
    },
    [isFilterApplied],
  );

  const menuItems = useMemo<MenuListItemType[]>(
    () =>
      filteredOptions.map((item) => {
        const isSelected = shouldHighlightFilter(item);
        const PremiumMarker = item.premium?.marker;

        return {
          id: item.id,
          'data-testid': TEST_IDS.helpers.createListItemId(item.id),
          onClick: () => {
            onFilterClick(item);
            setFilterValue('');
          },
          label: item.label,
          icon: (
            <>
              <FilterIcon className='w-15px h-15px' color='#969696' />
              {PremiumMarker && <PremiumMarker pillText={translate('filters.premium.listItemLabel')} />}
            </>
          ),
          disabled: false,
          selected: isSelected,
          logicalOperator: (item as Filter).logicalOperator,
          endAdornment: renderIcon(item),
        };
      }),
    [filteredOptions, onFilterClick, renderIcon, shouldHighlightFilter, translate],
  );

  return (
    <div className='overflow-hidden gap-1.5 flex flex-col'>
      {title && onBackClick && <SubFilterTabHeader onBackClick={onBackClick} title={title} />}
      <SearchFilterV2 className='shrink-0' onChange={setFilterValue} shouldClearField={!filterValue} />
      <MenuList noItemsMessage={translate('filters.noFiltersFound')} items={menuItems} />
      <FilterActionButtons onClose={closeDropdown} />
    </div>
  );
};
